/**
 * QMS Line Items Section
 * Collapsible table for managing line items on client tickets
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import type { QmsLineItem, QmsLineItemStatus } from '../../../types/qms.types';
import { useAddLineItem, useUpdateLineItem, useDeleteLineItem } from '../../../hooks/useQmsSubResources';

interface QmsLineItemsSectionProps {
  ticketId: string;
  lineItems: QmsLineItem[];
}

const LINE_ITEM_STATUSES: QmsLineItemStatus[] = ['preparing', 'ready', 'planning', 'in_progress', 'complete', 'cancelled'];

const emptyItem: Partial<QmsLineItem> = {
  status: 'preparing',
  sensorName: '',
  siteName: '',
  locationName: '',
  description: '',
  sku: '',
};

export function QmsLineItemsSection({ ticketId, lineItems }: QmsLineItemsSectionProps) {
  const { t } = useTranslation('qms');
  const [expanded, setExpanded] = useState(lineItems.length > 0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<QmsLineItem>>({});
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState<Partial<QmsLineItem>>(emptyItem);

  const addMutation = useAddLineItem(ticketId);
  const updateMutation = useUpdateLineItem(ticketId);
  const deleteMutation = useDeleteLineItem(ticketId);

  const handleAdd = async () => {
    try {
      await addMutation.mutateAsync(newItem);
      setIsAdding(false);
      setNewItem(emptyItem);
      toast.success(t('subResources.lineItems.added', 'Line item added'));
    } catch {
      toast.error(t('subResources.lineItems.addError', 'Failed to add line item'));
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    try {
      await updateMutation.mutateAsync({ itemId: editingId, data: editData });
      setEditingId(null);
      setEditData({});
      toast.success(t('subResources.lineItems.updated', 'Line item updated'));
    } catch {
      toast.error(t('subResources.lineItems.updateError', 'Failed to update line item'));
    }
  };

  const handleDelete = async (itemId: string) => {
    if (!confirm(t('subResources.confirmDelete', 'Delete this item?'))) return;
    try {
      await deleteMutation.mutateAsync(itemId);
      toast.success(t('subResources.lineItems.deleted', 'Line item deleted'));
    } catch {
      toast.error(t('subResources.lineItems.deleteError', 'Failed to delete line item'));
    }
  };

  const startEditing = (item: QmsLineItem) => {
    setEditingId(item.id);
    setEditData({ status: item.status, sensorName: item.sensorName, siteName: item.siteName, locationName: item.locationName, description: item.description, sku: item.sku });
  };

  return (
    <div className="border-t pt-4">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-base font-semibold text-gray-900 hover:text-atek-blue"
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {t('form.sections.lineItems', 'Line Items')}
        <span className="text-sm font-normal text-gray-500">({lineItems.length})</span>
      </button>

      {expanded && (
        <div className="mt-3">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.status', 'Status')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.sensorName', 'Sensor')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.siteName', 'Site')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.description', 'Description')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.sku', 'SKU')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item) => (
                  editingId === item.id ? (
                    <tr key={item.id} className="border-b border-gray-100 bg-blue-50">
                      <td className="px-2 py-1">
                        <select value={editData.status || ''} onChange={(e) => setEditData({ ...editData, status: e.target.value as QmsLineItemStatus })} className="w-full px-2 py-1 border rounded text-sm">
                          {LINE_ITEM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1"><input type="text" value={editData.sensorName || ''} onChange={(e) => setEditData({ ...editData, sensorName: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="text" value={editData.siteName || ''} onChange={(e) => setEditData({ ...editData, siteName: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="text" value={editData.description || ''} onChange={(e) => setEditData({ ...editData, description: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="text" value={editData.sku || ''} onChange={(e) => setEditData({ ...editData, sku: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1 flex gap-1">
                        <button type="button" onClick={handleUpdate} disabled={updateMutation.isPending} className="p-1 text-green-600 hover:bg-green-100 rounded"><Check className="h-4 w-4" /></button>
                        <button type="button" onClick={() => setEditingId(null)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900">
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${item.status === 'complete' ? 'bg-green-100 text-green-700' : item.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-900">{item.sensorName || '-'}</td>
                      <td className="px-3 py-2 text-gray-900">{item.siteName || '-'}</td>
                      <td className="px-3 py-2 text-gray-900">{item.description || '-'}</td>
                      <td className="px-3 py-2 text-gray-900">{item.sku || '-'}</td>
                      <td className="px-3 py-2 flex gap-1">
                        <button type="button" onClick={() => startEditing(item)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><Pencil className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => handleDelete(item.id)} disabled={deleteMutation.isPending} className="p-1 text-red-500 hover:bg-red-100 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  )
                ))}
                {isAdding && (
                  <tr className="border-b border-gray-100 bg-green-50">
                    <td className="px-2 py-1">
                      <select value={newItem.status || 'preparing'} onChange={(e) => setNewItem({ ...newItem, status: e.target.value as QmsLineItemStatus })} className="w-full px-2 py-1 border rounded text-sm">
                        {LINE_ITEM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1"><input type="text" value={newItem.sensorName || ''} onChange={(e) => setNewItem({ ...newItem, sensorName: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="Sensor" /></td>
                    <td className="px-2 py-1"><input type="text" value={newItem.siteName || ''} onChange={(e) => setNewItem({ ...newItem, siteName: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="Site" /></td>
                    <td className="px-2 py-1"><input type="text" value={newItem.description || ''} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="Description" /></td>
                    <td className="px-2 py-1"><input type="text" value={newItem.sku || ''} onChange={(e) => setNewItem({ ...newItem, sku: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="SKU" /></td>
                    <td className="px-2 py-1 flex gap-1">
                      <button type="button" onClick={handleAdd} disabled={addMutation.isPending} className="p-1 text-green-600 hover:bg-green-100 rounded"><Check className="h-4 w-4" /></button>
                      <button type="button" onClick={() => setIsAdding(false)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!isAdding && (
            <button type="button" onClick={() => setIsAdding(true)} className="mt-2 flex items-center gap-1 text-sm text-atek-blue hover:text-atek-blue-dark">
              <Plus className="h-4 w-4" /> {t('subResources.lineItems.add', 'Add Line Item')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
